import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHistory } from '../lib/api';
import { startGoogleLogin } from '../lib/auth';
import type { HistoryReading } from '../lib/types';
import UiIcon from '../components/common/UiIcon';

function spreadLabel(spread?: string) {
  switch (spread) {
    case 'one-card':
      return '한 장 리딩';
    case 'three-card':
      return '세 장 리딩';
    case 'celtic-cross':
      return '켈틱 크로스';
    default:
      return '리딩 기록';
  }
}

export default function HistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<HistoryReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [needLogin, setNeedLogin] = useState(false);

  useEffect(() => {
    let disposed = false;

    const fetchHistory = async () => {
      try {
        const response = await getHistory(30);
        if (disposed) return;
        setItems(response.readings);
        setErrorMessage(null);
        setNeedLogin(false);
      } catch (error) {
        if (disposed) return;
        const apiError = error as Error & { status?: number };
        if (apiError.status === 401) {
          setNeedLogin(true);
          setErrorMessage(null);
          return;
        }
        setErrorMessage(apiError.message || '히스토리를 불러오지 못했습니다.');
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void fetchHistory();
    return () => {
      disposed = true;
    };
  }, []);

  const handleLogin = () => {
    startGoogleLogin('/history');
  };

  return (
    <div className="min-h-screen bg-[#140f0d] px-6 py-12 text-[#f4e2cb]">
      <div className="mx-auto w-full max-w-3xl border border-[#8a6a4b]/40 bg-[#211712]/70 p-6 md:p-8">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#c9a092]">Private Reading Archive</p>
            <h1 className="mt-2 text-2xl font-bold text-[#fbbf24]">묘안 세션 아카이브</h1>
            <p className="mt-3 max-w-xl text-sm leading-7 text-[#dec5a8]/78">
              이미 읽은 리딩을 다시 펼쳐 보고, 그때의 흐름과 지금의 마음을 비교할 수 있는 개인 보관함입니다.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 border border-[#8a6a4b] bg-[#2a1d17] px-4 py-2 text-sm hover:bg-[#35251d]"
          >
            <UiIcon name="home" className="h-4 w-4" />
            홈으로
          </button>
        </div>

        {loading && (
          <p className="mt-6 text-sm text-[#dec5a8]/90">리딩 기록을 불러오는 중입니다.</p>
        )}

        {!loading && needLogin && (
          <div className="mt-6 border border-[#8a6a4b]/40 bg-[#19120f] p-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#fbbf24]/58">로그인 필요</p>
            <p className="mt-3 text-sm leading-7 text-[#dec5a8]/90">
              지난 리딩을 다시 보려면 Google 로그인이 필요합니다.
            </p>
            <button
              type="button"
              onClick={handleLogin}
              className="mt-4 border border-[#8a6a4b] bg-[#2a1d17] px-4 py-2 text-sm font-semibold hover:bg-[#35251d]"
            >
              Google 로그인
            </button>
          </div>
        )}

        {!loading && !needLogin && errorMessage && (
          <p className="mt-6 text-sm text-red-300">{errorMessage}</p>
        )}

        {!loading && !needLogin && !errorMessage && items.length === 0 && (
          <p className="mt-6 text-sm leading-7 text-[#dec5a8]/90">
            아직 저장된 리딩이 없습니다. 첫 리딩을 시작해 보세요.
          </p>
        )}

        {!loading && !needLogin && !errorMessage && items.length > 0 && (
          <div className="mt-6 space-y-4">
            {items.map((item) => (
              <article key={item.id} className="border border-[#8a6a4b]/35 bg-[#17110e] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]">
                      {new Date(item.created_at).toLocaleString('ko-KR')}
                    </p>
                    <span className="text-[11px] uppercase tracking-[0.16em] text-[#fbbf24]/70">
                      {spreadLabel(item.spread)}
                    </span>
                    <p className="mt-2 text-sm text-[#e8d8c1]/78">
                      카드가 한 장씩 펼쳐졌던 그 세션을 같은 순서로 다시 열 수 있습니다.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/spread-layout/${item.id}`)}
                    className="border border-[#8a6a4b] bg-[#2a1d17] px-3 py-2 text-xs font-semibold text-[#f3dfc5] hover:bg-[#35251d]"
                  >
                    다시 펼치기
                  </button>
                </div>
                <div className="mt-4 border border-[#8a6a4b]/25 bg-black/20 px-3 py-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#c9a092]/70">저장된 카드 흐름</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.cards.map((drawnCard, index) => (
                      <span
                        key={`${item.id}-${index}`}
                        className="inline-flex items-center gap-1 border border-[#8a6a4b]/50 bg-[#221913] px-2.5 py-1 text-xs text-[#e7d5bd]"
                      >
                        <span>{drawnCard.card.name_kr}</span>
                        <span className="text-[#c9a092]/80">
                          {drawnCard.orientation === 'upright' ? '정방향' : '역방향'}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
